import { io } from 'socket.io-client';
import { store } from '../store/store';
import {
  setConnectionStatus,
  setAuthenticationStatus,
  addMessage,
  addConversation,
  updateConversation,
  setUnreadCount,
  setError,
  setWidgetOpen,
  setActiveConversation
} from '../store/slices/chatSlice';

console.log('[Socket] Socket.IO client loaded:', typeof io);

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isAuthenticated = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }

  connect() {
    if (this.socket?.connected) {
      console.log('[Socket] Already connected');
      return;
    }

    const serverUrl = process.env.REACT_APP_SERVER_URL || 'https://localhost:4000';
    
    console.log('[Socket] Attempting connection...');
    console.log('[Socket] Server URL:', serverUrl);
    console.log('[Socket] Environment:', process.env.NODE_ENV);
    console.log('[Socket] All env vars starting with REACT_APP:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP')));

    this.socket = io(serverUrl, {
      transports: ['polling', 'websocket'], // Try polling first, then upgrade to websocket
      upgrade: true,
      rememberUpgrade: true,
      timeout: 30000, // Increased timeout to 30 seconds
      forceNew: false,
      reconnection: true,
      reconnectionDelay: this.reconnectDelay,
      reconnectionAttempts: this.maxReconnectAttempts,
      autoConnect: true
    });

    console.log('[Socket] Socket instance created, setting up event handlers...');
    console.log('[Socket] Socket object:', this.socket);
    console.log('[Socket] Socket connected:', this.socket.connected);
    console.log('[Socket] Socket id:', this.socket.id);
    this.setupEventHandlers();
  }

  setupEventHandlers() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('[Socket] ✅ Successfully connected with ID:', this.socket.id);
      console.log('[Socket] Connection transport:', this.socket.io.engine.transport.name);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      store.dispatch(setConnectionStatus(true));
      
      // Auto-authenticate if we have a token
      console.log('[Socket] Connection established, attempting authentication...');
      this.authenticateIfPossible();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Socket] ❌ Disconnected:', reason);
      this.isConnected = false;
      this.isAuthenticated = false;
      store.dispatch(setConnectionStatus(false));
      store.dispatch(setAuthenticationStatus(false));
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Socket] ❌ Connection error:', error);
      console.error('[Socket] Error type:', error.type);
      console.error('[Socket] Error description:', error.description);
      console.error('[Socket] Error context:', error.context);
      console.error('[Socket] Error transport:', error.transport);
      this.isConnected = false;
      store.dispatch(setConnectionStatus(false));
      store.dispatch(setError('Connection failed. Please check your internet connection.'));
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('[Socket] Reconnected after', attemptNumber, 'attempts');
      this.authenticateIfPossible();
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('[Socket] Reconnection error:', error);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('[Socket] Max reconnection attempts reached');
        store.dispatch(setError('Unable to reconnect. Please refresh the page.'));
      }
    });

    // Authentication events
    this.socket.on('authenticated', (data) => {
      console.log('[Socket] ✅ Authentication successful:', data);
      this.isAuthenticated = true;
      store.dispatch(setAuthenticationStatus(true));
    });

    this.socket.on('auth_error', (error) => {
      console.error('[Socket] ❌ Authentication error:', error);
      this.isAuthenticated = false;
      store.dispatch(setAuthenticationStatus(false));
      store.dispatch(setError(`Authentication failed: ${error}`));
    });

    // Chat events
    this.socket.on('new_message', (data) => {
      console.log('[Socket] New message received:', data);
      
      const { message, conversation } = data;
      
      // Add conversation if it doesn't exist
      if (conversation) {
        store.dispatch(addConversation(conversation));
      }
      
      // Add the new message
      store.dispatch(addMessage({
        conversationId: message.conversationId,
        message
      }));

      // Show browser notification for admin users
      this.showNotification(message, conversation);
    });

    this.socket.on('message_sent', (data) => {
      console.log('[Socket] Message sent confirmation:', data);
      // Message is already added via the API response
    });

    this.socket.on('messages_read', (data) => {
      console.log('[Socket] Messages read by another user:', data);
      // Update UI to show read status if needed
    });

    this.socket.on('unread_count', (data) => {
      console.log('[Socket] Unread count update:', data);
      store.dispatch(setUnreadCount(data.count));
    });

    this.socket.on('conversation_updated', (data) => {
      console.log('[Socket] Conversation updated:', data);
      store.dispatch(updateConversation({
        conversationId: data.conversationId,
        updates: data.updates
      }));
    });

    // Error handling
    this.socket.on('error', (error) => {
      console.error('[Socket] Error:', error);
      store.dispatch(setError(error));
    });
  }

  authenticateIfPossible() {
    const state = store.getState();
    const token = state.auth.token;
    const user = state.auth.user;

    console.log('[Socket] Authentication check:', {
      hasToken: !!token,
      hasUser: !!user,
      isConnected: this.isConnected,
      isAuthenticated: this.isAuthenticated,
      userRole: user?.role
    });

    if (!token) {
      console.log('[Socket] ❌ No token available for authentication');
      return;
    }

    if (!user) {
      console.log('[Socket] ❌ No user available for authentication');
      return;
    }

    if (!this.isConnected) {
      console.log('[Socket] ❌ Socket not connected, cannot authenticate');
      return;
    }

    if (this.isAuthenticated) {
      console.log('[Socket] ✅ Already authenticated');
      return;
    }

    // Check if user has admin privileges (handle both array and string role formats)
    const adminRoles = ['admin', 'owner', 'officeManager'];
    const userRole = user.role;
    const hasAdminRole = Array.isArray(userRole) 
      ? adminRoles.some(role => userRole.includes(role))
      : adminRoles.includes(userRole);
      
    console.log('[Socket] Role check:', {
      userRole,
      isArray: Array.isArray(userRole),
      hasAdminRole,
      adminRoles
    });
      
    if (hasAdminRole) {
      console.log('[Socket] ✅ User has admin privileges, authenticating...');
      this.authenticate(token);
    } else {
      console.log('[Socket] ❌ User does not have admin privileges for chat, role:', userRole);
    }
  }

  authenticate(token) {
    if (!this.socket || !this.isConnected) {
      console.warn('[Socket] ❌ Cannot authenticate - not connected');
      return;
    }

    console.log('[Socket] 📤 Sending authentication token...');
    this.socket.emit('authenticate', token);
    console.log('[Socket] Authentication token sent, waiting for response...');
  }

  joinConversation(conversationId) {
    if (!this.socket || !this.isAuthenticated) {
      console.warn('[Socket] Cannot join conversation - not authenticated');
      return;
    }

    console.log('[Socket] Joining conversation:', conversationId);
    this.socket.emit('join_conversation', conversationId);
  }

  leaveConversation(conversationId) {
    if (!this.socket || !this.isAuthenticated) {
      return;
    }

    console.log('[Socket] Leaving conversation:', conversationId);
    this.socket.emit('leave_conversation', conversationId);
  }

  markMessagesAsRead(conversationId, messageIds) {
    if (!this.socket || !this.isAuthenticated) {
      return;
    }

    console.log('[Socket] Marking messages as read:', { conversationId, messageIds });
    this.socket.emit('mark_messages_read', { conversationId, messageIds });
  }

  showNotification(message, conversation) {
    // Check if notifications are enabled
    const state = store.getState();
    const notificationSettings = state.chat.notifications;
    
    if (!notificationSettings.enabled) {
      return;
    }

    // Don't show notification for outgoing messages
    if (message.direction === 'outgoing') {
      return;
    }

    // Request permission if not granted
    if (Notification.permission === 'default') {
      Notification.requestPermission();
      return;
    }

    if (Notification.permission === 'granted') {
      const clientName = conversation?.client 
        ? `${conversation.client.firstName} ${conversation.client.lastName}`
        : 'Unknown Client';
      
      const phoneNumber = conversation?.phoneNumbers?.[0] || 'Unknown Number';
      
      const notification = new Notification(`New message from ${clientName}`, {
        body: message.body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: `sms-${message.conversationId}`,
        requireInteraction: false,
        silent: !notificationSettings.sound
      });

      notification.onclick = () => {
        window.focus();
        // Open chat widget and focus on conversation
        store.dispatch(setWidgetOpen(true));
        store.dispatch(setActiveConversation(message.conversationId));
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }

  disconnect() {
    if (this.socket) {
      console.log('[Socket] Disconnecting...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isAuthenticated = false;
      store.dispatch(setConnectionStatus(false));
      store.dispatch(setAuthenticationStatus(false));
    }
  }

  // Getters
  get connected() {
    return this.isConnected;
  }

  get authenticated() {
    return this.isAuthenticated;
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;